import { useState, useEffect } from 'react';

export const useViewportKey = (): 'desktop' | 'tablet' | 'mobile' => {
  const [viewportKey, setViewportKey] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      
      if (width < 768) setViewportKey('mobile');
      else if (width < 1024) setViewportKey('tablet');
      else setViewportKey('desktop');
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return viewportKey;
};

export const getRoleBadgeClass = (role: string): string => {
  const classMap: Record<string, string> = {
    owner: 'role-badge--owner',
    admin: 'role-badge--admin',
    writer: 'role-badge--member',
    member: 'role-badge--member',
    reader: 'role-badge--viewer',
    viewer: 'role-badge--viewer',
    pending: 'role-badge--pending',
  };

  return classMap[role] || 'role-badge--member';
};

export const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return '-';

  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};



const addAllPages = (totalPages: number): (number | string)[] => {
  const pages: (number | string)[] = [];

  for (let i = 1; i <= totalPages; i++) pages.push(i);

  return pages;
};

const addMiddlePages = (
  currentPage: number,
  totalPages: number,
  pageNumbers: (number | string)[]
) => {
  for (
    let i = Math.max(2, currentPage - 1);
    i <= Math.min(totalPages - 1, currentPage + 1);
    i++
  ) {
    if (!pageNumbers.includes(i)) pageNumbers.push(i);
  }
};

const addEllipsisAndEndPage = (
  pageNumbers: (number | string)[],
  currentPage: number,
  totalPages: number
): void => {
  if (currentPage < totalPages - 2) pageNumbers.push('...');
  if (!pageNumbers.includes(totalPages)) pageNumbers.push(totalPages);
};

const buildPageNumbersWithEllipsis = (currentPage: number, totalPages: number) => {
  const pageNumbers: (number | string)[] = [1];

  if (currentPage > 3) pageNumbers.push('...');
  addMiddlePages(currentPage, totalPages, pageNumbers);
  addEllipsisAndEndPage(pageNumbers, currentPage, totalPages);

  return pageNumbers;
};

export const calculatePageNumbers = (currentPage: number, totalPages: number): (number | string)[] => {
  const maxVisiblePages = 5;

  if (totalPages <= maxVisiblePages) return addAllPages(totalPages);

  return buildPageNumbersWithEllipsis(currentPage, totalPages);
};

export const calculatePaginationInfo = (
  currentPage: number,
  pageSize: number,
  totalCount: number
): { startItem: number; endItem: number } => {
  const startItem = (currentPage - 1) * pageSize + 1;

  const endItem = Math.min(currentPage * pageSize, totalCount);

  return { startItem, endItem };
};

export const getPageSizeForMode = (mode: 'grid' | 'list'): number => {
  const GRID_PAGE_SIZE = 12;

  const LIST_PAGE_SIZE = 10;

  return mode === 'grid' ? GRID_PAGE_SIZE : LIST_PAGE_SIZE;
};





export const saveViewModeToStorage = (mode: 'grid' | 'list', key: string): void => {
  localStorage.setItem(key, mode);
};

export const loadViewModeFromStorage = (key: string): 'grid' | 'list' => {
  const saved = localStorage.getItem(key);

  return saved === 'list' || saved === 'grid' ? saved : 'grid';
};
